import { Badge, Box, Button, HStack, Input, Text, Tooltip, VStack } from 'components/ui';
import { Checkbox } from 'components/ui/checkbox';
import { Spinner } from 'components/ui/spinner';
import { toaster } from 'components/ui/toaster';
import { getHelpVideoUrl } from 'config/helpVideos';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdContentCopy, MdWarning } from 'react-icons/md';
import { serverService } from 'services/serverService';
import { ToolConfig, toolService } from 'services/toolService';
import { getThemeColor } from 'theme/colors';

interface Step5ToolSelectionProps {
  serverId: string;
  onNext: (selectedToolIds: string[]) => void;
  initialData?: {
    selectedToolIds?: string[];
  };
}

export const Step5ToolSelection = ({
  serverId,
  onNext,
  initialData,
}: Step5ToolSelectionProps) => {
  const { t } = useTranslation();
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>(
    initialData?.selectedToolIds || []
  );
  const [isEnabling, setIsEnabling] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    loadTools();
  }, [serverId]);

  const loadTools = async () => {
    try {
      const toolsData = await toolService.getServerTools(serverId);
      setTools(toolsData);
    } catch (error: any) {
      toaster.create({
        title: t('app.onboarding.step5.loadToolsFailed'),
        description: error.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = tools.filter((tool) => {
    const search = searchTerm.toLowerCase();
    return (
      tool.name.toLowerCase().includes(search) ||
      tool.displayName?.toLowerCase().includes(search) ||
      tool.endpointPath.toLowerCase().includes(search) ||
      tool.httpMethod.toLowerCase().includes(search)
    );
  });

  const sortedTools = [...filteredTools].sort((a, b) => {
    // Method order priority
    const methodOrder: { [key: string]: number } = {
      GET: 1,
      POST: 2,
      PUT: 3,
      PATCH: 4,
      DELETE: 5,
    };

    const orderA = methodOrder[a.httpMethod] || 99;
    const orderB = methodOrder[b.httpMethod] || 99;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // Alphabetical by name within same method
    return a.name.localeCompare(b.name);
  });

  const toggleTool = (toolId: string) => {
    setValidationError('');
    if (selectedToolIds.includes(toolId)) {
      setSelectedToolIds(selectedToolIds.filter((id) => id !== toolId));
    } else {
      setSelectedToolIds([...selectedToolIds, toolId]);
    }
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'green';
      case 'POST':
        return 'blue';
      case 'PUT':
        return 'orange';
      case 'PATCH':
        return 'purple';
      case 'DELETE':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleNext = async () => {
    if (selectedToolIds.length === 0) {
      setValidationError(t('app.onboarding.step5.validationError'));
      return;
    }

    setIsEnabling(true);

    try {
      await serverService.bulkEnableTools(serverId, selectedToolIds);

      toaster.create({
        title: t('app.onboarding.step5.activateSuccess'),
        description: t('app.onboarding.step5.activatedCount', { count: selectedToolIds.length }),
        type: 'success',
      });

      onNext(selectedToolIds);
    } catch (error: any) {
      toaster.create({
        title: t('app.onboarding.step5.activateFailed'),
        description: error.message,
        type: 'error',
      });
      setIsEnabling(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="full">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={4}>
        <VStack gap={4} align="stretch">
          {/* Video Tutorial */}
          <Box
            maxW="840px"
            mx="auto"
            w="70%"
            mb={2}
          >
            <Box
              position="relative"
              w="full"
              paddingBottom="56.25%"
              overflow="hidden"
              borderRadius="lg"
              boxShadow="lg"
            >
              <iframe
                src={`https://www.youtube.com/embed/${getHelpVideoUrl('onboarding.step5.toolSelection')}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allowFullScreen
                title={t('app.onboarding.step5.videoTitle')}
              />
            </Box>
          </Box>

          {/* Sticky Search and Actions Bar */}
          <Box
            position="sticky"
            top={-4}
            bg={getThemeColor('bg.primary')}
            zIndex={10}
            py={3}
            mx={-6}
            px={6}
            borderBottomWidth="1px"
            borderColor={getThemeColor('border.default')}
          >
            <HStack gap={2} align="center">
              {/* Search */}
              <Input
                placeholder={t('app.onboarding.step5.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                flex={1}
              />

              {/* Quick Actions */}
              <Button
                size="xs"
                variant="outline"
                borderColor={getThemeColor('border.default')}
                onClick={() => {
                  const getToolIds = tools.filter((t) => t.httpMethod === 'GET').map((t) => t.id);
                  setSelectedToolIds(getToolIds);
                  setValidationError('');
                }}
              >
                {t('app.onboarding.step5.selectAllGet')}
              </Button>
              <Button
                size="xs"
                variant="outline"
                borderColor={getThemeColor('border.default')}
                onClick={() => {
                  setSelectedToolIds(tools.map((t) => t.id));
                  setValidationError('');
                }}
              >
                {t('app.onboarding.step5.selectAll')}
              </Button>
              <Button
                size="xs"
                variant="outline"
                borderColor={getThemeColor('border.default')}
                onClick={() => {
                  setSelectedToolIds([]);
                  setValidationError('');
                }}
              >
                {t('app.onboarding.step5.deselectAll')}
              </Button>
            </HStack>
          </Box>

          {/* Count */}
          <HStack justify="space-between" px={1}>
            <Text fontSize="xs" color={getThemeColor('text.secondary')}>
              {t('app.onboarding.step5.showingTools', { shown: sortedTools.length, total: tools.length })}
            </Text>
            <Text fontSize="xs" color="teal.600" fontWeight="semibold">
              {t('app.onboarding.step5.selectedCount', { count: selectedToolIds.length })}
            </Text>
          </HStack>

          {/* Validation Error */}
          {validationError && (
            <Box
              borderWidth="1px"
              borderColor={getThemeColor('alert.error.border')}
              bg={getThemeColor('alert.error.bg')}
              borderRadius="md"
              p={2}
            >
              <Text color={getThemeColor('alert.error.text')} fontSize="xs">
                {validationError}
              </Text>
            </Box>
          )}

          {/* Tools List - Integrated into main scroll */}
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={getThemeColor('border.default')}
            bg={getThemeColor('bg.primary')}
          >
            <VStack gap={0} align="stretch">
              {sortedTools.map((tool, index) => (
                <Box
                  key={tool.id}
                  borderBottomWidth={index < sortedTools.length - 1 ? '1px' : '0'}
                  borderColor={getThemeColor('border.default')}
                  p={3}
                  _hover={{ bg: getThemeColor('bg.hover') }}
                  cursor="pointer"
                  onClick={() => toggleTool(tool.id)}
                >
                  <HStack gap={3} align="start">
                    <Box pt={0.5} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedToolIds.includes(tool.id)}
                        onCheckedChange={() => toggleTool(tool.id)}
                        colorPalette="blue"
                        size="sm"
                      />
                    </Box>

                    <Badge
                      colorPalette={getMethodColor(tool.httpMethod)}
                      size="sm"
                      fontFamily="mono"
                      minW="60px"
                      textAlign="center"
                      flexShrink={0}
                    >
                      {tool.httpMethod}
                    </Badge>

                    <VStack align="start" gap={0.5} flex={1}>
                      <Text fontFamily="mono" fontSize="xs" color={getThemeColor('text.secondary')}>
                        {tool.endpointPath}
                      </Text>
                      <HStack gap={1.5} flexWrap="wrap">
                        <Text fontWeight="semibold" fontSize="sm" color={getThemeColor('text.primary')}>
                          {tool.displayName || tool.name}
                        </Text>
                        {tool.displayName && (
                          <Text fontSize="xs" color={getThemeColor('text.secondary')}>({tool.name})</Text>
                        )}
                        <Tooltip content={t(`app.servers.tools.riskTooltips.${tool.riskLevel}`)}>
                          <Badge
                            colorPalette={
                              tool.riskLevel === 'high' ? 'red' :
                              tool.riskLevel === 'medium' ? 'yellow' :
                              'green'
                            }
                            size="xs"
                            flexShrink={0}
                          >
                            {t(`app.servers.tools.riskLevels.${tool.riskLevel}`)}
                          </Badge>
                        </Tooltip>
                        {tool.isDestructive && (
                          <Tooltip content={t('app.servers.tools.destructiveTooltip')}>
                            <Badge colorPalette="red" size="xs" flexShrink={0}>
                              <MdWarning size={10} style={{ marginRight: '2px' }} />
                              {t('app.servers.tools.destructive')}
                            </Badge>
                          </Tooltip>
                        )}
                        {tool.isBulk && (
                          <Tooltip content={t('app.servers.tools.bulkTooltip')}>
                            <Badge colorPalette="purple" size="xs" flexShrink={0}>
                              <MdContentCopy size={10} style={{ marginRight: '2px' }} />
                              {t('app.servers.tools.bulk')}
                            </Badge>
                          </Tooltip>
                        )}
                      </HStack>
                      {tool.description && (
                        <Text fontSize="xs" color={getThemeColor('text.secondary')} css={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {tool.description}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>

            {sortedTools.length === 0 && (
              <Box textAlign="center" py={6}>
                <Text color={getThemeColor('text.secondary')} fontSize="sm">{t('app.onboarding.step5.noTools')}</Text>
              </Box>
            )}
          </Box>
        </VStack>
      </Box>

      {/* Sticky Footer */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor('border.default')}
        bg={getThemeColor('modal.bg')}
        py={4}
        px={6}
        boxShadow="sm"
      >
        <HStack justify="flex-end">
          <Button
            colorPalette="blue"
            onClick={handleNext}
            loading={isEnabling}
            size="md"
            disabled={selectedToolIds.length === 0 || isEnabling}
          >
            {isEnabling
              ? t('app.onboarding.step5.activating')
              : t('app.onboarding.step5.next')}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};